"use client";

import { getThemeClassName } from "@/theming/selectors";
import { isHTMLElement } from "@/tracking/utils";
import classNames from "classnames";
import { useEffect, useState, type JSX } from "react";

type Children = JSX.Element | JSX.Element[] | string;

type HalfCordionProps = {
  children?: Children;
  className?: string;
  maxHeightClass?: string;
  themeClass?: string;
  expandBtnText?: string;
  intersectionObserverRoot: string;
  fadeText?: boolean;
};

export const HalfCordion = ({
  children,
  className,
  maxHeightClass = "max-h-44",
  themeClass = getThemeClassName("body"),
  expandBtnText = "Les mer",
  intersectionObserverRoot,
  fadeText = false,
}: HalfCordionProps) => {
  const [halfCordionOpen, setHalfCordionOpen] = useState(false);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    if (!halfCordionOpen) {
      document.querySelectorAll(intersectionObserverRoot).forEach((item) => {
        const observer = new IntersectionObserver(
          (entry) => {
            if (
              entry.some(({ isIntersecting }) => isIntersecting) &&
              isHTMLElement(item) &&
              !halfCordionOpen
            ) {
              setHalfCordionOpen(true);
            }
          },
          {
            threshold: 1,
          },
        );
        observer.observe(item);
        observers.push(observer);
      });
    }
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [halfCordionOpen, intersectionObserverRoot]);

  return (
    <>
      <div
        className={classNames(
          "relative overflow-hidden pb-6",
          {
            [maxHeightClass]: !halfCordionOpen,
          },
          className,
        )}
        aria-expanded={halfCordionOpen}
      >
        {children}
        {!halfCordionOpen && (
          <div
            className={classNames("absolute bottom-0 w-full", {
              "h-full bg-linear-to-t from-[var(--bg-body)]": fadeText,
            })}
          >
            {fadeText && <div className="w-ful h-5/6"></div>}
            <button
              className={classNames(
                "w-full py-4 text-left underline",
                themeClass,
              )}
              onClick={() => setHalfCordionOpen(!halfCordionOpen)}
            >
              {expandBtnText}
            </button>
          </div>
        )}
      </div>
    </>
  );
};
